import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// In-memory cache with localStorage backup for offline support
const cache = new Map();
const CACHE_DURATION = 1000; // 1 second for live data
const OFFLINE_CACHE_KEY = 'fleethub_api_cache';

// Load cache from localStorage on startup
try {
  const savedCache = localStorage.getItem(OFFLINE_CACHE_KEY);
  if (savedCache) {
    const parsed = JSON.parse(savedCache);
    Object.entries(parsed).forEach(([key, value]) => {
      cache.set(key, value);
    });
  }
} catch (e) {
  console.log('[API] Failed to load offline cache');
}

// Save cache to localStorage periodically
const saveCacheToStorage = () => {
  try {
    const cacheObj = {};
    cache.forEach((value, key) => {
      // Only save GET requests that are important
      if (key.startsWith('get:') && !key.includes('/typing') && !key.includes('/health')) {
        cacheObj[key] = value;
      }
    });
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(cacheObj));
  } catch (e) {
    console.log('[API] Failed to save cache');
  }
};

// Save cache every 10 seconds
setInterval(saveCacheToStorage, 10000);

const getCacheKey = (config) => `${config.method}:${config.url}`;

const getFromCache = (key, forOffline = false) => {
  const cached = cache.get(key);
  if (!cached) return null;
  
  // For offline mode, return any cached data regardless of age
  if (forOffline || !navigator.onLine) {
    return cached.data;
  }
  
  // For online mode, check cache duration
  if (Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for specific URL pattern
const clearCache = (urlPattern) => {
  for (const key of cache.keys()) {
    if (key.includes(urlPattern)) {
      cache.delete(key);
    }
  }
};

// Clear all cache
const clearAllCache = () => {
  cache.clear();
  localStorage.removeItem(OFFLINE_CACHE_KEY);
};

// Configuration avec timeout réduit pour plus de réactivité
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000, // 10 secondes timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Nombre de tentatives de reconnexion
const MAX_RETRIES = 2; // Réduit pour plus de réactivité
const RETRY_DELAY = 500; // 500ms entre chaque tentative

// Fonction pour attendre
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Flag to prevent multiple simultaneous redirects
let isRedirecting = false;

// Add auth token to requests and handle caching
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add retry count to config
    config.retryCount = config.retryCount || 0;
    
    // Check cache for GET requests
    if (config.method === 'get' && !config.skipCache) {
      const cacheKey = getCacheKey(config);
      const isOffline = !navigator.onLine;
      const cachedData = getFromCache(cacheKey, isOffline);
      
      if (cachedData) {
        // If offline, always return cached data
        if (isOffline) {
          config.adapter = () => Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK (cached)',
            headers: {},
            config,
            cached: true,
            offline: true
          });
        } else {
          // Online: return cache but also fetch in background
          config.adapter = () => Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            cached: true
          });
        }
      } else if (isOffline) {
        // Offline with no cache - return error
        config.adapter = () => Promise.reject({
          response: {
            status: 503,
            data: { error: 'offline', message: 'Données non disponibles hors ligne' }
          },
          config,
          offline: true
        });
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle errors with retry logic and cache successful responses
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && !response.cached && !response.config.skipCache) {
      const cacheKey = getCacheKey(response.config);
      setCache(cacheKey, response.data);
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Si c'est une erreur 401 (non autorisé)
    if (error.response?.status === 401) {
      // Skip redirect for refresh and login endpoints
      const isAuthEndpoint = config.url?.includes('/auth/refresh') || 
                            config.url?.includes('/auth/login') ||
                            config.url?.includes('/auth/me');
      
      if (!isAuthEndpoint && !isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Ne pas rediriger si on est déjà sur la page login
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        // Reset after a short delay
        setTimeout(() => { isRedirecting = false; }, 1000);
      }
      return Promise.reject(error);
    }
    
    // Ne pas retry pour les erreurs 4xx (erreurs client)
    if (error.response?.status >= 400 && error.response?.status < 500) {
      return Promise.reject(error);
    }
    
    // Retry pour les erreurs réseau ou 5xx
    const shouldRetry = (
      !error.response || // Erreur réseau
      error.response.status >= 500 || // Erreur serveur
      error.code === 'ECONNABORTED' || // Timeout
      error.code === 'ERR_NETWORK' // Erreur réseau
    );
    
    if (shouldRetry && config && config.retryCount < MAX_RETRIES) {
      config.retryCount += 1;
      console.log(`Tentative de reconnexion ${config.retryCount}/${MAX_RETRIES}...`);
      
      // Attendre avant de réessayer (délai exponentiel)
      await sleep(RETRY_DELAY * config.retryCount);
      
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour vérifier la connexion au serveur
export const checkServerConnection = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${BACKEND_URL}/api/health`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store'
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      return data?.status === 'ok' || data?.status === 'healthy';
    }
    return false;
  } catch (error) {
    console.error('Health check failed:', error.message);
    return false;
  }
};

// Fonction pour rafraîchir le token
export const refreshToken = async () => {
  try {
    const response = await api.post('/auth/refresh');
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error.message);
    return null;
  }
};

// Export cache utilities
export { clearCache, clearAllCache };

export default api;
